import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Users, Trophy, MessageSquare,
  Plus, Pencil, Trash2, X, Check, ChevronDown, AlertTriangle,
  GraduationCap, BarChart2, Eye, EyeOff, Save, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SEO from '../components/SEO';

// ─── Reusable Modal ───────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
    <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
        <h3 className="font-heading font-bold text-xl">{title}</h3>
        <button onClick={onClose} className="p-1 hover:text-aspire-red transition-colors">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

// ─── Confirm Delete Modal ─────────────────────────────────────────────────────
const ConfirmDelete = ({ item, onConfirm, onCancel }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <div className="bg-white w-full max-w-sm shadow-2xl p-8 text-center">
      <AlertTriangle className="w-12 h-12 text-aspire-red mx-auto mb-4" />
      <h3 className="font-heading font-bold text-xl mb-2">Delete Confirmation</h3>
      <p className="text-gray-600 mb-6">Are you sure you want to delete <strong>"{item}"</strong>? This cannot be undone.</p>
      <div className="flex gap-3">
        <button onClick={onCancel} className="flex-1 py-3 border-2 border-gray-300 font-bold uppercase tracking-widest text-sm hover:border-black transition-colors">Cancel</button>
        <button onClick={onConfirm} className="flex-1 py-3 bg-aspire-red text-white font-bold uppercase tracking-widest text-sm hover:bg-red-800 transition-colors">Delete</button>
      </div>
    </div>
  </div>
);

// ─── Field Input Helper ───────────────────────────────────────────────────────
const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const Input = ({ ...props }) => (
  <input className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-aspire-red focus:outline-none focus:ring-1 focus:ring-aspire-red bg-gray-50" {...props} />
);

const Textarea = ({ ...props }) => (
  <textarea className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-aspire-red focus:outline-none focus:ring-1 focus:ring-aspire-red bg-gray-50 resize-none" rows={3} {...props} />
);

const Select = ({ children, ...props }) => (
  <select className="w-full border border-gray-300 px-3 py-2 text-sm focus:border-aspire-red focus:outline-none bg-gray-50" {...props}>
    {children}
  </select>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color = 'text-aspire-red' }) => (
  <div className="bg-white border border-gray-100 p-6 shadow-sm">
    <div className="flex items-center gap-4">
      <div className={`w-12 h-12 bg-gray-50 flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</p>
        <p className="font-heading font-bold text-3xl text-charcoal">{value}</p>
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SUBJECTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const SubjectsTab = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', level: 'IGCSE', exam_board: 'Cambridge',
    description: '', overview: '', target_grades: 'A* - B',
    duration: '12 weeks', session_duration: '2 hours',
    group_price_local: 2500, group_price_international: 4000,
    individual_price_physical_local: 3500, individual_price_online_local: 3000,
    individual_price_international: 5000, max_group_size: 8,
    tutor_name: '', is_active: true
  });

  const load = async () => {
    try {
      const { data, error } = await supabase.from('subjects').select('*');
      if (error) throw error;
      setSubjects(data);
    } catch (e) { toast.error('Failed to load subjects'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: '', slug: '', level: 'IGCSE', exam_board: 'Cambridge', description: '', overview: '', target_grades: 'A* - B', duration: '12 weeks', session_duration: '2 hours', group_price_local: 2500, group_price_international: 4000, individual_price_physical_local: 3500, individual_price_online_local: 3000, individual_price_international: 5000, max_group_size: 8, tutor_name: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (s) => {
    setEditItem(s);
    setForm({ ...s });
    setShowModal(true);
  };

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleSave = async () => {
    if (!form.name || !form.slug) return toast.error('Name and slug are required');
    setSaving(true);
    try {
      if (editItem) {
        const { error } = await supabase.from('subjects').update(form).eq('subject_id', editItem.subject_id);
        if (error) throw error;
        toast.success('Subject updated');
      } else {
        const { error } = await supabase.from('subjects').insert(form);
        if (error) throw error;
        toast.success('Subject created');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to save subject');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('subjects').delete().eq('subject_id', deleteItem.subject_id);
      if (error) throw error;
      toast.success('Subject deleted');
      setDeleteItem(null);
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-aspire-red" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-sm">{subjects.length} subjects total</p>
        <button onClick={openCreate} className="flex items-center gap-2 bg-aspire-red text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-red-800 transition-colors">
          <Plus className="w-4 h-4" /> Add Subject
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Subject</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Level</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Board</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Group (LK)</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Tutor</th>
              <th className="text-left py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Status</th>
              <th className="text-right py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map(s => (
              <tr key={s.subject_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4 font-semibold">{s.name}</td>
                <td className="py-3 px-4"><span className="font-mono text-xs bg-gray-100 px-2 py-0.5">{s.level}</span></td>
                <td className="py-3 px-4 text-gray-600">{s.exam_board}</td>
                <td className="py-3 px-4 font-mono text-aspire-red font-bold">Rs. {s.group_price_local?.toLocaleString()}</td>
                <td className="py-3 px-4 text-gray-600">{s.tutor_name || '—'}</td>
                <td className="py-3 px-4">
                  <span className={`text-xs font-bold px-2 py-1 ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {s.is_active ? 'Active' : 'Hidden'}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(s)} className="p-1.5 hover:text-aspire-red transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteItem(s)} className="p-1.5 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editItem ? `Edit: ${editItem.name}` : 'Create New Subject'} onClose={() => setShowModal(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Subject Name">
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value, slug: autoSlug(e.target.value) }))} placeholder="e.g. Mathematics" />
            </Field>
            <Field label="URL Slug">
              <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="e.g. igcse-mathematics" />
            </Field>
            <Field label="Level">
              <Select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}>
                <option>IGCSE</option><option>AS</option><option>A2</option>
              </Select>
            </Field>
            <Field label="Exam Board">
              <Select value={form.exam_board} onChange={e => setForm(p => ({ ...p, exam_board: e.target.value }))}>
                <option>Cambridge</option><option>Edexcel</option><option>Cambridge & Edexcel</option>
              </Select>
            </Field>
            <div className="col-span-2">
              <Field label="Short Description">
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description for subject cards..." />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Full Overview">
                <Textarea value={form.overview || ''} onChange={e => setForm(p => ({ ...p, overview: e.target.value }))} rows={4} placeholder="Detailed course overview..." />
              </Field>
            </div>
            <Field label="Target Grades">
              <Input value={form.target_grades || ''} onChange={e => setForm(p => ({ ...p, target_grades: e.target.value }))} placeholder="e.g. A* - B" />
            </Field>
            <Field label="Tutor Name">
              <Input value={form.tutor_name || ''} onChange={e => setForm(p => ({ ...p, tutor_name: e.target.value }))} placeholder="e.g. Dinura Abeygunawardena" />
            </Field>

            <div className="col-span-2 border-t border-gray-100 pt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Pricing (Rs.)</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Group — Local"><Input type="number" value={form.group_price_local} onChange={e => setForm(p => ({ ...p, group_price_local: +e.target.value }))} /></Field>
                <Field label="Group — International"><Input type="number" value={form.group_price_international} onChange={e => setForm(p => ({ ...p, group_price_international: +e.target.value }))} /></Field>
                <Field label="Individual Physical (Local)"><Input type="number" value={form.individual_price_physical_local} onChange={e => setForm(p => ({ ...p, individual_price_physical_local: +e.target.value }))} /></Field>
                <Field label="Individual Online (Local)"><Input type="number" value={form.individual_price_online_local} onChange={e => setForm(p => ({ ...p, individual_price_online_local: +e.target.value }))} /></Field>
                <Field label="Individual — International"><Input type="number" value={form.individual_price_international} onChange={e => setForm(p => ({ ...p, individual_price_international: +e.target.value }))} /></Field>
                <Field label="Max Group Size"><Input type="number" value={form.max_group_size} onChange={e => setForm(p => ({ ...p, max_group_size: +e.target.value }))} /></Field>
              </div>
            </div>

            <div className="col-span-2 flex items-center gap-3 pt-2">
              <button
                onClick={() => setForm(p => ({ ...p, is_active: !p.is_active }))}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors ${form.is_active ? 'border-green-600 text-green-700 bg-green-50' : 'border-gray-300 text-gray-500'}`}
              >
                {form.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {form.is_active ? 'Active — Visible' : 'Hidden'}
              </button>
            </div>

            <div className="col-span-2 flex gap-3 pt-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 border-2 border-gray-300 font-bold uppercase tracking-widest text-sm hover:border-black transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-3 bg-aspire-red text-white font-bold uppercase tracking-widest text-sm hover:bg-red-800 transition-colors flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editItem ? 'Save Changes' : 'Create Subject'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleteItem && <ConfirmDelete item={deleteItem.name} onConfirm={handleDelete} onCancel={() => setDeleteItem(null)} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// TUTORS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const TutorsTab = () => {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', qualification: '', subjects: [], bio: '', experience: '', image_url: '', availability: '', session_format: 'Online & In-Person' });
  const [subjectInput, setSubjectInput] = useState('');

  const load = async () => {
    try {
      const { data, error } = await supabase.from('tutors').select('*');
      if (error) throw error;
      setTutors(data);
    } catch (e) { toast.error('Failed to load tutors'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditItem(null); setForm({ name: '', qualification: '', subjects: [], bio: '', experience: '', image_url: '', availability: '', session_format: 'Online & In-Person' }); setSubjectInput(''); setShowModal(true); };
  const openEdit = (t) => { setEditItem(t); setForm({ ...t }); setSubjectInput(''); setShowModal(true); };

  const addSubject = () => {
    if (subjectInput.trim() && !form.subjects.includes(subjectInput.trim())) {
      setForm(p => ({ ...p, subjects: [...p.subjects, subjectInput.trim()] }));
      setSubjectInput('');
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.qualification) return toast.error('Name and qualification are required');
    setSaving(true);
    try {
      if (editItem) {
        const { error } = await supabase.from('tutors').update(form).eq('tutor_id', editItem.tutor_id);
        if (error) throw error;
        toast.success('Tutor updated');
      } else {
        const { error } = await supabase.from('tutors').insert(form);
        if (error) throw error;
        toast.success('Tutor added');
      }
      setShowModal(false); load();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('tutors').delete().eq('tutor_id', deleteItem.tutor_id);
      if (error) throw error;
      toast.success('Tutor deleted'); setDeleteItem(null); load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-aspire-red" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-sm">{tutors.length} tutors total</p>
        <button onClick={openCreate} className="flex items-center gap-2 bg-aspire-red text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-red-800 transition-colors">
          <Plus className="w-4 h-4" /> Add Tutor
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tutors.map(t => (
          <div key={t.tutor_id} className="border border-gray-100 p-5 bg-white hover:border-aspire-red transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 bg-aspire-red/10 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-aspire-red" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(t)} className="p-1.5 hover:text-aspire-red transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => setDeleteItem(t)} className="p-1.5 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <h4 className="font-heading font-bold text-base">{t.name}</h4>
            <p className="text-xs text-gray-500 mt-1 mb-3">{t.qualification}</p>
            <div className="flex flex-wrap gap-1">
              {t.subjects?.map(s => <span key={s} className="text-xs bg-gray-100 px-2 py-0.5 font-mono">{s}</span>)}
            </div>
            {t.experience && <p className="text-xs text-gray-400 mt-2">{t.experience} experience</p>}
          </div>
        ))}
      </div>

      {showModal && (
        <Modal title={editItem ? `Edit: ${editItem.name}` : 'Add New Tutor'} onClose={() => setShowModal(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full Name"><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Dinura Abeygunawardena" /></Field>
            <Field label="Experience"><Input value={form.experience || ''} onChange={e => setForm(p => ({ ...p, experience: e.target.value }))} placeholder="e.g. 5 years" /></Field>
            <div className="col-span-2">
              <Field label="Qualification"><Input value={form.qualification} onChange={e => setForm(p => ({ ...p, qualification: e.target.value }))} placeholder="e.g. BSc Mechatronics & Economics, QUT" /></Field>
            </div>
            <div className="col-span-2">
              <Field label="Bio"><Textarea value={form.bio || ''} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder="Brief tutor bio..." /></Field>
            </div>
            <Field label="Session Format"><Input value={form.session_format || ''} onChange={e => setForm(p => ({ ...p, session_format: e.target.value }))} placeholder="Online & In-Person" /></Field>
            <Field label="Availability"><Input value={form.availability || ''} onChange={e => setForm(p => ({ ...p, availability: e.target.value }))} placeholder="e.g. Weekdays & Weekends" /></Field>
            <div className="col-span-2">
              <Field label="Photo URL"><Input value={form.image_url || ''} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." /></Field>
            </div>
            <div className="col-span-2">
              <Field label="Subjects Taught">
                <div className="flex gap-2 mb-2">
                  <input value={subjectInput} onChange={e => setSubjectInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSubject()} className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:border-aspire-red focus:outline-none bg-gray-50" placeholder="Type subject and press Enter or Add" />
                  <button onClick={addSubject} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 transition-colors text-sm font-bold">Add</button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.subjects?.map(s => (
                    <span key={s} className="flex items-center gap-1 bg-aspire-red/10 text-aspire-red text-xs px-2 py-1 font-medium">
                      {s}
                      <button onClick={() => setForm(p => ({ ...p, subjects: p.subjects.filter(x => x !== s) }))}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </Field>
            </div>
            <div className="col-span-2 flex gap-3 pt-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 border-2 border-gray-300 font-bold uppercase tracking-widest text-sm hover:border-black transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-3 bg-aspire-red text-white font-bold uppercase tracking-widest text-sm hover:bg-red-800 transition-colors flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editItem ? 'Save Changes' : 'Add Tutor'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {deleteItem && <ConfirmDelete item={deleteItem.name} onConfirm={handleDelete} onCancel={() => setDeleteItem(null)} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESULTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const ResultsTab = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ student_name: '', subject: '', grade: 'A*', level: 'IGCSE', exam_board: 'Cambridge', year: new Date().getFullYear(), image_url: '', testimonial: '' });

  const load = async () => {
    try {
      const { data, error } = await supabase.from('results').select('*');
      if (error) throw error;
      setResults(data);
    } catch (e) { toast.error('Failed to load results'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditItem(null); setForm({ student_name: '', subject: '', grade: 'A*', level: 'IGCSE', exam_board: 'Cambridge', year: new Date().getFullYear(), image_url: '', testimonial: '' }); setShowModal(true); };
  const openEdit = (r) => { setEditItem(r); setForm({ ...r }); setShowModal(true); };

  const handleSave = async () => {
    if (!form.subject || !form.grade) return toast.error('Subject and grade are required');
    setSaving(true);
    try {
      if (editItem) {
        const { error } = await supabase.from('results').update(form).eq('result_id', editItem.result_id);
        if (error) throw error;
        toast.success('Result updated');
      } else {
        const { error } = await supabase.from('results').insert(form);
        if (error) throw error;
        toast.success('Result added');
      }
      setShowModal(false); load();
    } catch (e) { toast.error(e.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase.from('results').delete().eq('result_id', deleteItem.result_id);
      if (error) throw error;
      toast.success('Result deleted'); setDeleteItem(null); load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-aspire-red" /></div>;

  const gradeColors = { 'A*': 'bg-emerald-100 text-emerald-800', 'A': 'bg-green-100 text-green-700', 'B': 'bg-blue-100 text-blue-700', 'C': 'bg-amber-100 text-amber-700' };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-gray-500 text-sm">{results.length} results total</p>
        <button onClick={openCreate} className="flex items-center gap-2 bg-aspire-red text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-red-800 transition-colors">
          <Plus className="w-4 h-4" /> Add Result
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              {['Photo', 'Student', 'Subject', 'Grade', 'Level', 'Board', 'Year', 'Actions'].map(h => (
                <th key={h} className={`py-3 px-4 text-xs font-bold uppercase tracking-widest text-gray-400 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.map(r => (
              <tr key={r.result_id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.student_name || 'Student'} className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-100" />
                  )}
                </td>
                <td className="py-3 px-4 font-medium">{r.student_name || 'Anonymous'}</td>
                <td className="py-3 px-4">{r.subject}</td>
                <td className="py-3 px-4"><span className={`font-mono font-bold text-sm px-2 py-0.5 ${gradeColors[r.grade] || 'bg-gray-100 text-gray-700'}`}>{r.grade}</span></td>
                <td className="py-3 px-4 text-gray-600">{r.level}</td>
                <td className="py-3 px-4 text-gray-600">{r.exam_board}</td>
                <td className="py-3 px-4 font-mono text-gray-600">{r.year}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:text-aspire-red transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteItem(r)} className="p-1.5 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editItem ? 'Edit Result' : 'Add Result'} onClose={() => setShowModal(false)}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Student Name (optional)"><Input value={form.student_name || ''} onChange={e => setForm(p => ({ ...p, student_name: e.target.value }))} placeholder="Leave blank for anonymous" /></Field>
            <Field label="Subject"><Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Mathematics" /></Field>
            <Field label="Grade">
              <Select value={form.grade} onChange={e => setForm(p => ({ ...p, grade: e.target.value }))}>
                {['A*', 'A', 'B', 'C', 'D', 'E', 'U'].map(g => <option key={g}>{g}</option>)}
              </Select>
            </Field>
            <Field label="Level">
              <Select value={form.level} onChange={e => setForm(p => ({ ...p, level: e.target.value }))}>
                <option>IGCSE</option><option>AS</option><option>A2</option><option>A Level</option>
              </Select>
            </Field>
            <Field label="Exam Board">
              <Select value={form.exam_board} onChange={e => setForm(p => ({ ...p, exam_board: e.target.value }))}>
                <option>Cambridge</option><option>Edexcel</option>
              </Select>
            </Field>
            <Field label="Year"><Input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: +e.target.value }))} /></Field>
            <div className="col-span-2">
              <Field label="Student Photo URL (optional)">
                <Input value={form.image_url || ''} onChange={e => setForm(p => ({ ...p, image_url: e.target.value }))} placeholder="https://..." />
              </Field>
              <p className="text-xs text-gray-400 mt-1">
                One photo per student. If this student has multiple subjects, only set it on one of their result entries &mdash; the results page will use it for all of them automatically.
              </p>
            </div>
            <div className="col-span-2">
              <Field label="Testimonial (optional)"><Textarea value={form.testimonial || ''} onChange={e => setForm(p => ({ ...p, testimonial: e.target.value }))} placeholder="Student's quote about their experience..." /></Field>
            </div>
            <div className="col-span-2 flex gap-3 pt-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-3 border-2 border-gray-300 font-bold uppercase tracking-widest text-sm hover:border-black transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-3 bg-aspire-red text-white font-bold uppercase tracking-widest text-sm hover:bg-red-800 transition-colors flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editItem ? 'Save Changes' : 'Add Result'}
              </button>
            </div>
          </div>
        </Modal>
      )}
      {deleteItem && <ConfirmDelete item={`${deleteItem.subject} — ${deleteItem.grade}`} onConfirm={handleDelete} onCancel={() => setDeleteItem(null)} />}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONTACTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data, error } = await supabase.from('inquiries').select('*').order('created_at', { ascending: false });
        if (error) throw error;
        setContacts(data);
      } catch (e) {
        setContacts([]);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-aspire-red" /></div>;

  if (contacts.length === 0) {
    return (
      <div className="text-center py-20 text-gray-400">
        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-40" />
        <p className="font-heading font-bold text-lg text-gray-500">No contact submissions yet</p>
        <p className="text-sm mt-1">When visitors fill in the contact form, their messages will appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {contacts.map((c, i) => (
        <div key={i} className="border border-gray-100 p-6 bg-white hover:border-aspire-red transition-colors">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-bold">{c.name}</h4>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span>{c.email}</span>
                {c.phone && <span>{c.phone}</span>}
                {c.subject && <span className="font-mono bg-gray-100 px-2 py-0.5 text-xs">{c.subject}</span>}
              </div>
            </div>
            <span className="text-xs text-gray-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
          </div>
          {c.message && <p className="text-sm text-gray-600 bg-gray-50 p-3 border-l-2 border-aspire-red">{c.message}</p>}
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════
const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ subjects: 0, tutors: 0, results: 0 });

  useEffect(() => {
    if (profile && profile.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    const loadStats = async () => {
      try {
        const [s, t, r] = await Promise.all([
          supabase.from('subjects').select('*', { count: 'exact', head: true }),
          supabase.from('tutors').select('*', { count: 'exact', head: true }),
          supabase.from('results').select('*', { count: 'exact', head: true }),
        ]);
        setStats({
          subjects: s.count || 0,
          tutors: t.count || 0,
          results: r.count || 0
        });
      } catch (e) { /* silently fail */ }
    };
    loadStats();
  }, [profile, navigate]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'subjects', label: 'Subjects', icon: BookOpen },
    { id: 'tutors', label: 'Tutors', icon: GraduationCap },
    { id: 'results', label: 'Results', icon: Trophy },
    { id: 'contacts', label: 'Contacts', icon: MessageSquare },
  ];

  return (
    <main className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" path="/admin" noIndex />
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="container-main py-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="label-uppercase text-aspire-red">Admin</span>
              <h1 className="font-heading font-bold text-3xl mt-1">Content Management</h1>
            </div>
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-500">
              <div className="w-8 h-8 bg-aspire-red/10 flex items-center justify-center">
                <Users className="w-4 h-4 text-aspire-red" />
              </div>
              <span className="font-medium">{profile?.name || user?.email}</span>
              <span className="bg-aspire-red text-white text-xs font-bold px-2 py-0.5 uppercase tracking-wider">Admin</span>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 mt-6 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`admin-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-widest whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-aspire-red text-aspire-red bg-red-50'
                    : 'border-transparent text-gray-500 hover:text-charcoal hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-main py-8">
        {activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <StatCard icon={BookOpen} label="Subjects" value={stats.subjects} />
              <StatCard icon={GraduationCap} label="Tutors" value={stats.tutors} />
              <StatCard icon={Trophy} label="Results" value={stats.results} />
              <StatCard icon={MessageSquare} label="Contacts" value={stats.contacts || 0} />
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {tabs.filter(t => t.id !== 'overview').map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="group border border-gray-100 bg-white p-6 text-left hover:border-aspire-red transition-all shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-aspire-red/10 flex items-center justify-center group-hover:bg-aspire-red transition-colors">
                      <tab.icon className="w-6 h-6 text-aspire-red group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Manage {tab.label}</h3>
                      <p className="text-sm text-gray-500">Add, edit, or remove {tab.label.toLowerCase()}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'subjects' && <SubjectsTab />}
        {activeTab === 'tutors' && <TutorsTab />}
        {activeTab === 'results' && <ResultsTab />}
        {activeTab === 'contacts' && <ContactsTab />}
      </div>
    </main>
  );
};

export default AdminDashboardPage;
